import React, { useState, useEffect } from 'react';
import '../css/App.css';
import WebAppConsent from './WebAppConsent';

// ── Simulated external website preview ────────────────────────────────────────
function ExternalSitePreview({ appName, tenantId, mode }) {
  const [widgetState, setWidgetState] = useState('hidden'); // hidden | popup | iframe | granted | denied
  const [consentToken, setConsentToken] = useState(null);
  const [copied, setCopied] = useState(false);

  // Auto-trigger widget when mode changes
  useEffect(() => {
    setWidgetState('hidden');
    setConsentToken(null);
  }, [mode]);

  const handleGrantConsent = () => {
    const token = 'tok_' + Math.random().toString(36).slice(2, 10).toUpperCase();
    setConsentToken(token);
    setWidgetState('granted');
  };

  const handleDenyConsent = () => {
    setWidgetState('denied');
  };

  const triggerWidget = () => {
    setWidgetState(mode === 'popup' ? 'popup' : mode === 'iframe' ? 'iframe' : 'redirect');
  };

  const embedCode = `<script src="https://consent.whitehats.tech/widget/web-consent-widget.js"></script>
<script>
  WebConsentWidget.init({
    apiBase: 'https://consent.whitehats.tech/api',
    appName: '${appName}',
    tenantId: ${tenantId},
    mode: '${mode}',
    onConsentGranted: function(token) {
      console.log('Consent granted:', token);
    },
    onConsentDenied: function() {
      console.log('Consent denied');
    }
  });
</script>`;

  const copyCode = () => {
    navigator.clipboard.writeText(embedCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={ps.wrap}>
      {/* Simulated browser chrome */}
      <div style={ps.browserBar}>
        <div style={ps.browserDots}>
          <span style={{ ...ps.dot, background: '#ef4444' }} />
          <span style={{ ...ps.dot, background: '#f59e0b' }} />
          <span style={{ ...ps.dot, background: '#22c55e' }} />
        </div>
        <div style={ps.addressBar}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ marginRight: 6 }}>
            <rect x="2" y="7" width="12" height="8" rx="2" stroke="#94a3b8" strokeWidth="1.5" />
            <path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          https://yourwebsite.com
        </div>
      </div>

      {/* Fake website content */}
      <div style={ps.site}>
        <div style={ps.siteNav}>
          <div style={ps.siteLogo} />
          <div style={{ display: 'flex', gap: 16 }}>
            {['Home', 'About', 'Services', 'Contact'].map(l => (
              <div key={l} style={ps.navLink}>{l}</div>
            ))}
          </div>
        </div>

        <div style={ps.siteHero}>
          <div style={ps.heroLine1} />
          <div style={ps.heroLine2} />
          <div style={ps.heroLine3} />
          <button style={ps.heroBtn} onClick={triggerWidget}>
            Login to {appName}
          </button>
          {widgetState === 'granted' && (
            <div style={ps.grantedBanner}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <polyline points="3,8 6,11 13,4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Consent granted — Token: <code style={{ fontFamily: 'monospace', fontSize: 11 }}>{consentToken}</code>
            </div>
          )}
          {widgetState === 'denied' && (
            <div style={{ ...ps.grantedBanner, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M4 4l8 8M12 4l-8 8" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Consent denied — access blocked
            </div>
          )}
        </div>

        <div style={ps.siteParagraphs}>
          {[80, 60, 70, 50, 65].map((w, i) => (
            <div key={i} style={{ ...ps.para, width: `${w}%` }} />
          ))}
        </div>

        {/* ── POPUP MODE ── */}
        {widgetState === 'popup' && (
          <div style={ps.popupOverlay}>
            <div style={ps.popupWin}>
              <div style={ps.popupHeader}>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>Consent Required — {appName}</span>
                <button style={ps.popupClose} onClick={() => setWidgetState('hidden')}>✕</button>
              </div>
              <ConsentMiniForm appName={appName} onGrant={handleGrantConsent} onDeny={handleDenyConsent} />
            </div>
          </div>
        )}

        {/* ── IFRAME MODE ── */}
        {widgetState === 'iframe' && (
          <div style={ps.iframeOverlay}>
            <div style={ps.iframeModal}>
              <button style={ps.iframeClose} onClick={() => setWidgetState('hidden')}>✕</button>
              <ConsentMiniForm appName={appName} onGrant={handleGrantConsent} onDeny={handleDenyConsent} isIframe />
            </div>
          </div>
        )}

        {/* ── REDIRECT MODE ── */}
        {widgetState === 'redirect' && (
          <div style={ps.redirectPage}>
            <div style={ps.redirectCard}>
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M20 4L6 10v8c0 9 6.3 16 14 18 7.7-2 14-9 14-18v-8L20 4z" fill="#dbeafe" />
                <polyline points="13,20 17,24 27,14" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', margin: '12px 0 4px' }}>Redirected to consent page</p>
              <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>User fills consent form and is redirected back</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button style={ps.miniBtn} onClick={handleGrantConsent}>Simulate: Grant</button>
                <button style={{ ...ps.miniBtn, background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }} onClick={handleDenyConsent}>Simulate: Deny</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Embed code section */}
      <div style={ps.embedSection}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Embed code for your website</span>
          <button style={{ ...ps.miniBtn, fontSize: 12 }} onClick={copyCode}>
            {copied ? '✓ Copied!' : 'Copy code'}
          </button>
        </div>
        <pre style={ps.codeBlock}>{embedCode}</pre>
      </div>
    </div>
  );
}

// ── Mini consent form used inside popup / iframe preview ──────────────────────
function ConsentMiniForm({ appName, onGrant, onDeny, isIframe }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [step, setStep] = useState(1); // 1 = fill form, 2 = done

  const handleSubmit = () => {
    if (!email || !name) return;
    setStep(2);
    setTimeout(() => onGrant(), 900);
  };

  if (step === 2) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 20px' }}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" style={{ margin: '0 auto 10px', display: 'block' }}>
          <circle cx="18" cy="18" r="17" fill="#dcfce7" />
          <polyline points="10,18 15,23 26,12" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Consent recorded!</p>
        <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Redirecting back…</p>
      </div>
    );
  }

  return (
    <div style={{ padding: isIframe ? '20px 28px' : '16px 20px' }}>
      <p style={{ fontSize: 12, color: '#64748b', marginBottom: 14, lineHeight: 1.5 }}>
        <strong style={{ color: '#0f172a' }}>{appName}</strong> requires your consent before granting access.
      </p>
      <div style={{ marginBottom: 10 }}>
        <div style={mf.label}>Email *</div>
        <input style={mf.input} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={mf.label}>Full name *</div>
        <input style={mf.input} type="text" placeholder="John Doe" value={name} onChange={e => setName(e.target.value)} />
      </div>
      <div style={mf.consentNote}>
        By clicking below I consent to use <strong>{appName}</strong> and agree to data processing terms.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={mf.grantBtn} onClick={handleSubmit}>Grant consent</button>
        <button style={mf.denyBtn} onClick={onDeny}>Deny</button>
      </div>
    </div>
  );
}

const mf = {
  label: { fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input: { width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: '#0f172a', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  consentNote: { fontSize: 11, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', marginBottom: 12, lineHeight: 1.5 },
  grantBtn: { flex: 1, padding: '8px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  denyBtn: { padding: '8px 12px', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
};

// ── Preview styles ─────────────────────────────────────────────────────────────
const ps = {
  wrap: { border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff', fontFamily: "'DM Sans', system-ui, sans-serif" },
  browserBar: { background: '#f1f5f9', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #e2e8f0' },
  browserDots: { display: 'flex', gap: 5 },
  dot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block' },
  addressBar: { flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 20, padding: '3px 12px', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center' },
  site: { position: 'relative', background: '#f8fafc', minHeight: 340, overflow: 'hidden' },
  siteNav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: '#fff', borderBottom: '1px solid #f1f5f9' },
  siteLogo: { width: 80, height: 14, background: '#e2e8f0', borderRadius: 4 },
  navLink: { width: 48, height: 10, background: '#f1f5f9', borderRadius: 3 },
  siteHero: { padding: '28px 20px 16px', textAlign: 'center' },
  heroLine1: { width: '55%', height: 16, background: '#e2e8f0', borderRadius: 4, margin: '0 auto 10px' },
  heroLine2: { width: '40%', height: 12, background: '#f1f5f9', borderRadius: 4, margin: '0 auto 10px' },
  heroLine3: { width: '30%', height: 10, background: '#f1f5f9', borderRadius: 4, margin: '0 auto 16px' },
  heroBtn: { padding: '9px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  grantedBanner: { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#16a34a' },
  siteParagraphs: { padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 8 },
  para: { height: 8, background: '#e2e8f0', borderRadius: 3 },
  popupOverlay: { position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 },
  popupWin: { background: '#fff', borderRadius: 10, width: 320, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' },
  popupHeader: { background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  popupClose: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#94a3b8', padding: 0 },
  iframeOverlay: { position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 },
  iframeModal: { background: '#fff', borderRadius: 12, width: '85%', maxWidth: 380, position: 'relative', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  iframeClose: { position: 'absolute', top: 10, right: 12, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 },
  redirectPage: { position: 'absolute', inset: 0, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 },
  redirectCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '28px 24px', textAlign: 'center', maxWidth: 280 },
  miniBtn: { padding: '6px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  embedSection: { borderTop: '1px solid #e2e8f0', padding: '16px 20px', background: '#f8fafc' },
  codeBlock: { background: '#1e293b', color: '#e2e8f0', borderRadius: 8, padding: '12px 14px', fontSize: 11, lineHeight: 1.7, overflowX: 'auto', margin: 0, fontFamily: 'monospace', whiteSpace: 'pre' },
};

// ── Main page ──────────────────────────────────────────────────────────────────
function WebConsentPage({ onBack }) {
  const [appName, setAppName] = useState('HR Portal');
  const [tenantId, setTenantId] = useState(1);
  const [mode, setMode] = useState('popup');

  return (
    <div className="cookie-consent-page">
      {/* Header */}
      <div className="page-header" style={{ alignItems: 'center', marginBottom: '1.5rem', marginTop: '-0.25rem' }}>
        <h2 className="page-title" style={{ margin: 0 }}>Web Consent Management</h2>
        <button className="back-btn" onClick={() => typeof onBack === 'function' ? onBack() : window.history.back()}>
          Back
        </button>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* LEFT — Consent form (admin fills / tests) */}
        <div style={{ flex: '1 1 360px', minWidth: 320 }}>
          <div style={layout.sectionLabel}>Consent Form</div>
          <div style={layout.card}>
            <WebAppConsent />
          </div>
        </div>

        {/* RIGHT — Live preview of external site */}
        <div style={{ flex: '1 1 380px', minWidth: 320 }}>
          <div style={layout.sectionLabel}>External Website Preview</div>

          {/* Config controls */}
          <div style={layout.configRow}>
            <div style={layout.configItem}>
              <label style={layout.configLabel}>App name</label>
              <input
                style={layout.configInput}
                value={appName}
                onChange={e => setAppName(e.target.value)}
                placeholder="Your App Name"
              />
            </div>
            <div style={layout.configItem}>
              <label style={layout.configLabel}>Tenant ID</label>
              <input
                style={{ ...layout.configInput, width: 70 }}
                type="number"
                value={tenantId}
                onChange={e => setTenantId(Number(e.target.value))}
              />
            </div>
            <div style={layout.configItem}>
              <label style={layout.configLabel}>Widget mode</label>
              <select style={layout.configInput} value={mode} onChange={e => setMode(e.target.value)}>
                <option value="popup">Popup window</option>
                <option value="iframe">Iframe modal</option>
                <option value="redirect">Redirect</option>
              </select>
            </div>
          </div>

          <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
            Click <strong>"Login to {appName}"</strong> inside the preview to trigger the consent widget.
          </p>

          <ExternalSitePreview appName={appName} tenantId={tenantId} mode={mode} />
        </div>
      </div>
    </div>
  );
}

const layout = {
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 },
  card: { border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' },
  configRow: { display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' },
  configItem: { display: 'flex', flexDirection: 'column', gap: 4 },
  configLabel: { fontSize: 11, fontWeight: 600, color: '#64748b' },
  configInput: { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, color: '#0f172a', background: '#fff', fontFamily: 'inherit', outline: 'none' },
};

export default WebConsentPage;